// Wrapper around sonner with TVO theme (#013b6d navy + #14b8c4 cyan)
import { Toaster as SonnerToaster, toast } from 'sonner'
import type { ToasterProps } from 'sonner'

export type { ToasterProps }
export { toast }

/**
 * Shim TC-compatible — `useToast()` devuelve un objeto con métodos `success`,
 * `error`, `warning`, `info`. Internamente delega en sonner. Permite portar
 * código de TC sin reescribirlo.
 *
 * R15b: añadido `addToast({ type, title, message, duration? })` para paridad
 * con la API de Toast de TC.
 */
type ToastType = 'success' | 'error' | 'warning' | 'info'
interface AddToastInput {
  type: ToastType
  title: string
  message?: string
  duration?: number
}

export function useToast() {
  const fire = (kind: ToastType, title: string, message?: string, duration?: number) => {
    const opts: { description?: string; duration?: number } = {}
    if (message) opts.description = message
    if (duration != null) opts.duration = duration
    return toast[kind](title, opts)
  }
  return {
    success: (title: string, message?: string) => fire('success', title, message),
    error: (title: string, message?: string) => fire('error', title, message),
    warning: (title: string, message?: string) => fire('warning', title, message),
    info: (title: string, message?: string) => fire('info', title, message),
    addToast: ({ type, title, message, duration }: AddToastInput) =>
      fire(type, title, message, duration),
  }
}

/**
 * <Toaster /> globalmente. Montar UNA SOLA VEZ en main.tsx.
 * Resto del código importa `import { toast } from '@/components/ui/Toast'`
 * (o desde 'sonner' directamente, ambos válidos).
 */
export function Toaster(props: ToasterProps) {
  return (
    <SonnerToaster
      position="top-right"
      richColors
      closeButton
      theme="light"
      toastOptions={{
        classNames: {
          toast:
            'group bg-white border border-neutral-200 shadow-lg rounded-lg text-sm',
          title: 'font-semibold text-neutral-900',
          description: 'text-neutral-600',
          actionButton:
            'bg-[#013b6d] text-white hover:bg-[#024a87] rounded-md px-3 py-1.5 text-xs font-medium',
          cancelButton:
            'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 rounded-md px-3 py-1.5 text-xs font-medium',
          closeButton:
            'bg-white border border-neutral-200 text-neutral-500 hover:bg-neutral-100',
          success: 'group-[.toaster]:border-emerald-200!',
          error: 'group-[.toaster]:border-red-200!',
          warning: 'group-[.toaster]:border-amber-200!',
          info: 'group-[.toaster]:border-blue-200!',
        },
      }}
      {...props}
    />
  )
}
